import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Copy,
  EnvelopeSimple,
  ArrowClockwise,
  XCircle,
  UploadSimple,
  Trash,
  FloppyDisk,
  DownloadSimple,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { formatEventSchedule, milestoneContent } from "@/lib/milestones";
import { useGet } from "@/hooks/useResource";
import { PlanningInputsEditor } from "@/components/staff/PlanningInputsEditor";
import { EventHeroImageUpload } from "@/components/staff/EventHeroImageUpload";
import { EventInvoicesTab } from "@/components/staff/EventInvoicesTab";
import { RunningOrderEditor } from "@/components/staff/RunningOrderEditor";
import { Loading, formatAuditTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

const M_STATUS = [
  { v: "upcoming", l: "Upcoming" },
  { v: "in_progress", l: "In progress" },
  { v: "complete", l: "Complete" },
];
// Running order, invoices, floor plans and forms each have their own tab/page, so the event
// Documents tab keeps only the two categories that genuinely live here.
const DOC_CATS = [
  { v: "run_sheet", l: "Run Sheet" },
  { v: "other", l: "Other" },
];
const DOC_CATS_VISIBLE = new Set(DOC_CATS.map((c) => c.v));

export default function EventDetail() {
  const { id } = useParams();
  const { data, loading, refetch } = useGet(`/staff/events/${id}`);
  const [invite, setInvite] = useState(null);
  const eventSchedule = formatEventSchedule(
    data?.event?.event_date,
    data?.event?.event_time,
  );
  const copyEventId = () => {
    navigator.clipboard.writeText(id);
    toast.success("Event ID copied.");
  };

  if (loading) return <Loading label="Loading event" />;
  if (!data?.event)
    return <p className="text-muted-foreground">Event not found.</p>;

  return (
    <div>
      <Link
        to="/events"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-200 mb-6"
      >
        <ArrowLeft size={18} /> All events
      </Link>
      <p className="eyebrow text-accent">{data.event.event_type}</p>
      <h1
        className="font-heading text-5xl sm:text-6xl font-normal leading-none text-primary mt-2"
        data-testid="staff-event-couple-names"
      >
        {data.event.couple_names}
      </h1>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
        <span data-testid="staff-event-schedule">{eventSchedule}</span>
        <span aria-hidden="true">·</span>
        <span>{data.event.venue}</span>
        <button
          type="button"
          onClick={copyEventId}
          className="inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs text-primary hover:bg-muted"
          data-testid="copy-event-id-btn"
        >
          <Copy size={13} />
          <span className="font-mono break-all" data-testid="event-id-value">
            {id}
          </span>
        </button>
      </div>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="overview" data-testid="tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="milestones" data-testid="tab-milestones">
            Milestones
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            Documents
          </TabsTrigger>
          <TabsTrigger value="running-order" data-testid="tab-running-order">
            Running Order
          </TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">
            Clients
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            Invoices
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab data={data} eventId={id} refetch={refetch} />
        </TabsContent>
        <TabsContent value="milestones" className="mt-6">
          <MilestonesTab data={data} eventId={id} refetch={refetch} />
        </TabsContent>
        <TabsContent value="documents" className="mt-6">
          <DocumentsTab data={data} eventId={id} refetch={refetch} />
        </TabsContent>
        <TabsContent value="running-order" className="mt-6">
          <RunningOrderEditor eventId={id} />
        </TabsContent>
        <TabsContent value="clients" className="mt-6">
          <ClientsTab
            data={data}
            eventId={id}
            refetch={refetch}
            setInvite={setInvite}
          />
        </TabsContent>
        <TabsContent value="invoices" className="mt-6">
          <EventInvoicesTab
            eventId={id}
            invoices={data.invoices || []}
            onSaved={refetch}
          />
        </TabsContent>
      </Tabs>

      <InviteDialog invite={invite} onClose={() => setInvite(null)} />
    </div>
  );
}

/* ---------- Clients ---------- */
function ClientsTab({ data, eventId, refetch, setInvite }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "",
    name: "",
    event_role: "client_primary",
  });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!form.email.trim()) {
      toast.error("Email is required.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/staff/events/${eventId}/clients`, form);
      setInvite(r.data);
      setOpen(false);
      setForm({ email: "", name: "", event_role: "client_primary" });
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const reissue = async (mid) => {
    try {
      const r = await api.post(`/staff/clients/${mid}/reissue`);
      setInvite(r.data);
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const revoke = async (mid) => {
    try {
      await api.post(`/staff/clients/${mid}/revoke`);
      toast.success("Access revoked.");
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between gap-3 items-center mb-4">
        <p className="text-sm text-muted-foreground">
          Up to two client contacts per event.
        </p>
        <Button
          className="rounded-full bg-primary hover:bg-primary/90"
          onClick={() => setOpen(true)}
          data-testid="add-client-btn"
        >
          <Plus size={18} className="mr-1" /> Add client
        </Button>
      </div>
      <div className="space-y-3">
        {data.memberships.length === 0 && (
          <p className="text-muted-foreground">No client contacts yet.</p>
        )}
        {data.memberships.map((m) => (
          <div
            key={m.membership_id}
            data-testid={`member-${m.membership_id}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5"
          >
            <div>
              <p className="font-medium text-primary">{m.name || m.email}</p>
              <p className="text-sm text-muted-foreground">{m.email}</p>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline" className="rounded-full capitalize">
                  {m.event_role.replace("client_", "").replace("_", " ")}
                </Badge>
                <Badge
                  variant="outline"
                  className={`rounded-full capitalize ${m.status === "active" ? "text-accent-foreground border-accent/30 bg-accent/10" : m.status === "revoked" ? "text-destructive border-destructive/20 bg-destructive/5" : ""}`}
                >
                  {m.status}
                </Badge>
                {m.password_is_temporary && (
                  <Badge
                    variant="outline"
                    className="rounded-full"
                    style={{
                      backgroundColor: "#FEF3C7",
                      color: "#92400E",
                      borderColor: "#FDE68A",
                    }}
                  >
                    Temp password
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => reissue(m.membership_id)}
                data-testid={`reissue-${m.membership_id}`}
              >
                <ArrowClockwise size={16} className="mr-1" /> Reissue
              </Button>
              {m.status !== "revoked" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full text-destructive"
                  onClick={() => revoke(m.membership_id)}
                  data-testid={`revoke-${m.membership_id}`}
                >
                  <XCircle size={16} className="mr-1" /> Revoke
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">
              Add client contact
            </DialogTitle>
            <DialogDescription>
              A temporary password will be generated for new accounts. You'll
              send the invitation yourself.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="eyebrow">Email</Label>
              <Input
                className="mt-2"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="client-email-input"
              />
            </div>
            <div>
              <Label className="eyebrow">Name</Label>
              <Input
                className="mt-2"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="client-name-input"
              />
            </div>
            <div>
              <Label className="eyebrow">Role</Label>
              <Select
                value={form.event_role}
                onValueChange={(v) => setForm({ ...form, event_role: v })}
              >
                <SelectTrigger
                  className="mt-2"
                  data-testid="client-role-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client_primary">
                    Primary contact
                  </SelectItem>
                  <SelectItem value="client_partner">Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="rounded-full bg-primary hover:bg-primary/90"
              onClick={add}
              disabled={busy}
              data-testid="save-client-btn"
            >
              {busy ? "Adding…" : "Add & prepare invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InviteDialog({ invite, onClose }) {
  if (!invite) return null;
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  };
  return (
    <Dialog open={!!invite} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Invitation prepared
          </DialogTitle>
          <DialogDescription>
            Send this to {invite.email} yourself. No email has been sent
            automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {invite.temp_password ? (
            <div
              className="rounded-xl p-4"
              style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}
            >
              <p className="eyebrow" style={{ color: "#92400E" }}>
                Temporary password (shown once)
              </p>
              <div className="flex items-center justify-between gap-2 mt-1">
                <code
                  className="font-mono text-lg"
                  data-testid="temp-password-value"
                >
                  {invite.temp_password}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => copy(invite.temp_password)}
                >
                  <Copy size={16} />
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              This person already has an account — they sign in with their
              existing password.
            </p>
          )}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="eyebrow">Invitation text</Label>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copy(invite.invite_text)}
                data-testid="copy-invite-btn"
              >
                <Copy size={14} className="mr-1" /> Copy
              </Button>
            </div>
            <Textarea
              readOnly
              rows={6}
              value={invite.invite_text}
              className="text-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <a href={invite.mailto} data-testid="mailto-link">
            <Button variant="outline" className="rounded-full">
              <EnvelopeSimple size={18} className="mr-1" /> Open email draft
            </Button>
          </a>
          <Button
            className="rounded-full bg-primary hover:bg-primary/90"
            onClick={onClose}
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Milestones ---------- */
function MilestonesTab({ data, eventId, refetch }) {
  const [ms, setMs] = useState(data.event.milestones || []);
  const [busy, setBusy] = useState(false);
  const setStatus = (key, status) =>
    setMs((prev) => prev.map((m) => (m.key === key ? { ...m, status } : m)));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/staff/events/${eventId}/milestones`, {
        milestones: ms.map((m) => ({ key: m.key, status: m.status })),
      });
      toast.success("Milestones updated.");
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">
          Set each milestone's status. Clients see this read-only.
        </p>
        <Button
          className="rounded-full bg-primary hover:bg-primary/90"
          onClick={save}
          disabled={busy}
          data-testid="save-milestones-btn"
        >
          <FloppyDisk size={18} className="mr-1" /> {busy ? "Saving…" : "Save"}
        </Button>
      </div>
      <div className="rounded-2xl border border-border bg-card divide-y divide-border">
        {ms.map((m, i) => (
          <div
            key={m.key}
            className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-[1fr_10rem] sm:items-center"
            data-testid={`milestone-row-${m.key}`}
          >
            <span className="text-sm text-primary">
              {i + 1}. {m.label}
              <span
                className="mt-1 block max-w-2xl text-xs text-muted-foreground"
                data-testid={`milestone-description-${m.key}`}
              >
                {milestoneContent(m).description}
              </span>
              {m.status === "complete" && m.completed_at && (
                <span
                  className="mt-1 block text-xs text-muted-foreground"
                  data-testid={`milestone-completed-at-${m.key}`}
                >
                  Completed {formatAuditTime(m.completed_at)}
                </span>
              )}
            </span>
            <Select value={m.status} onValueChange={(v) => setStatus(m.key, v)}>
              <SelectTrigger
                className="w-full"
                data-testid={`milestone-select-${m.key}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {M_STATUS.map((s) => (
                  <SelectItem key={s.v} value={s.v}>
                    {s.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Documents ---------- */
function DocumentsTab({ data, eventId, refetch }) {
  const [category, setCategory] = useState("run_sheet");
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post(`/staff/events/${eventId}/documents`, fd);
      toast.success("Document uploaded.");
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (docId) => {
    try {
      await api.delete(`/staff/documents/${docId}`);
      toast.success("Document removed.");
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const download = async (doc) => {
    try {
      const response = await api.get(`/staff/documents/${doc.id}/file`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.original_filename || "event-document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const active = data.documents.filter(
    (d) => d.status === "active" && DOC_CATS_VISIBLE.has(d.category),
  );
  const approval = data.run_sheet_approval;

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-6 rounded-2xl border border-border bg-card p-5">
        <div>
          <Label className="eyebrow">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger
              className="w-48 mt-2"
              data-testid="doc-category-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOC_CATS.map((c) => (
                <SelectItem key={c.v} value={c.v}>
                  {c.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label className="inline-flex">
          <input
            type="file"
            accept=".pdf,.docx,.xlsx,.csv,.png,.jpg,.jpeg,.webp"
            hidden
            onChange={upload}
            data-testid="doc-file-input"
          />
          <span
            className={`inline-flex items-center rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 text-sm cursor-pointer transition-colors duration-200 ${busy ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="upload-doc-btn"
          >
            <UploadSimple size={18} className="mr-1" />{" "}
            {busy ? "Uploading…" : "Upload document"}
          </span>
        </label>
        {approval && (
          <div
            className="text-sm sm:ml-auto"
            data-testid="staff-runsheet-approval"
          >
            <span className="text-muted-foreground">
              Run sheet v{approval.version}:{" "}
            </span>
            <Badge
              variant="outline"
              className="rounded-full capitalize"
              data-testid="staff-runsheet-status"
            >
              {approval.state}
            </Badge>
            {approval.approved_by && (
              <p className="mt-1 text-muted-foreground" data-testid="staff-runsheet-approved-at">
                Approved by {approval.approved_by} · {formatAuditTime(approval.updated_at)}
              </p>
            )}
          </div>
        )}
      </div>

      {approval?.history?.length > 0 && (
        <section
          className="mb-6 border border-border bg-card p-5 rounded-lg"
          data-testid="staff-runsheet-history"
        >
          <p className="eyebrow">Run sheet approval trail</p>
          <ol className="mt-3 space-y-2 text-sm">
            {approval.history
              .slice()
              .reverse()
              .map((entry, index) => (
                <li
                  key={`${entry.at}-${index}`}
                  className="flex flex-wrap justify-between gap-x-4 gap-y-1 border-b border-border pb-2 last:border-0 last:pb-0"
                >
                  <span className="font-medium capitalize text-primary">
                    {entry.state}
                  </span>
                  <span className="text-muted-foreground">
                    {formatAuditTime(entry.at)}
                  </span>
                </li>
              ))}
          </ol>
        </section>
      )}

      {active.length === 0 ? (
        <p className="text-muted-foreground">No documents uploaded yet.</p>
      ) : (
        <div className="rounded-2xl border border-border bg-card divide-y divide-border">
          {active.map((d) => (
            <div
              key={d.id}
              className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
              data-testid={`doc-${d.id}`}
            >
              <div>
                <p className="font-medium text-primary">
                  {d.original_filename}
                </p>
                <div className="flex gap-2 mt-1">
                  <Badge variant="outline" className="rounded-full capitalize">
                    {d.category.replace("_", " ")}
                  </Badge>
                  <Badge variant="outline" className="rounded-full">
                    v{d.version}
                  </Badge>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => download(d)}
                  data-testid={`download-doc-${d.id}`}
                  className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted transition-colors duration-200"
                  aria-label={`Download ${d.original_filename}`}
                >
                  <DownloadSimple size={18} />
                </button>
                <button
                  onClick={() => remove(d.id)}
                  data-testid={`delete-doc-${d.id}`}
                  className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-200"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Overview / review ---------- */
function OverviewTab({ data, eventId, refetch }) {
  const [form, setForm] = useState({
    event_type: data.event.event_type || "",
    couple_names: data.event.couple_names || "",
    event_date: data.event.event_date || "",
    event_time: data.event.event_time || "",
    venue: data.event.venue || "",
    notes: data.event.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/staff/events/${eventId}`, form);
      toast.success("Event updated.");
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const p = data.planning || {};
  const milestones = data.event.milestones || [];
  const complete = milestones.filter((m) => m.status === "complete").length;
  const current =
    milestones.find((m) => m.status === "in_progress") ||
    milestones.find((m) => m.status === "upcoming");
  const nextAction =
    data.memberships.length === 0
      ? "Invite the first client contact"
      : !data.event.event_date
        ? "Confirm the event date and time"
        : data.run_sheet_approval?.state === "pending"
          ? "Follow up on run sheet approval"
          : current?.label || "Review completed planning";
  return (
    <div className="space-y-6">
      <section
        className="grid grid-cols-1 gap-4 rounded-2xl border border-border bg-card p-5 sm:grid-cols-3"
        data-testid="staff-event-overview-summary"
      >
        <div>
          <p className="eyebrow mb-1">Milestones</p>
          <p className="font-heading text-2xl text-primary">
            {complete} of {milestones.length || 13}
          </p>
        </div>
        <div>
          <p className="eyebrow mb-1">Current focus</p>
          <p className="font-medium text-primary">
            {current?.label || "Planning journey complete"}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Staff controls statuses.
          </p>
        </div>
        <div data-testid="staff-event-next-action">
          <p className="eyebrow mb-1">Next action</p>
          <p className="font-medium text-primary">{nextAction}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Open the relevant tab to continue.
          </p>
        </div>
      </section>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex justify-between items-center mb-4">
            <p className="eyebrow">Event details</p>
            <Button
              size="sm"
              className="rounded-full bg-primary hover:bg-primary/90"
              onClick={save}
              disabled={busy}
              data-testid="save-event-details-btn"
            >
              <FloppyDisk size={16} className="mr-1" /> Save
            </Button>
          </div>
          <div className="space-y-3">
            <Field
              label="Event type"
              v={form.event_type}
              on={(x) => setForm({ ...form, event_type: x })}
            />
            <Field
              label="Couple / event name"
              v={form.couple_names}
              on={(x) => setForm({ ...form, couple_names: x })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Date"
                type="date"
                v={form.event_date}
                on={(x) => setForm({ ...form, event_date: x })}
              />
              <Field
                label="Time"
                type="time"
                v={form.event_time}
                on={(x) => setForm({ ...form, event_time: x })}
              />
            </div>
            <Field
              label="Venue"
              v={form.venue}
              on={(x) => setForm({ ...form, venue: x })}
            />
            <EventHeroImageUpload
              eventId={eventId}
              event={data.event}
              onSaved={refetch}
            />
            <div>
              <Label className="eyebrow">Notes</Label>
              <Textarea
                className="mt-2"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
        </div>

        <PlanningInputsEditor
          eventId={eventId}
          planning={p}
          runSheetApproval={data.run_sheet_approval}
          onSaved={refetch}
        />
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text" }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <Input
        type={type}
        className="mt-2"
        value={v}
        onChange={(e) => on(e.target.value)}
      />
    </div>
  );
}
